import { useEffect } from 'react'
import mapboxgl from 'mapbox-gl'
import {
  calculateDistance,
  getMidpoint,
  extractCoordinates,
} from '../../utils/geometry'
import { getBuildingClassificationColor } from '../../../../../utils/buildingClassificationColors'

interface Layer {
  id: string
  enabled: boolean
  visible?: boolean
}

interface UseBuildingLayersProps {
  map: React.MutableRefObject<mapboxgl.Map | null>
  mapLoaded: boolean
  buildingGeoJson?: any
  buildingHeight?: number
  buildingClassification?: string
  layers: Layer[]
  styleLoadedCount: number
}

/**
 * Hook to handle building layers (footprints, 3D extrusion, and dimension metrics)
 */
export const useBuildingLayers = ({
  map,
  mapLoaded,
  buildingGeoJson,
  buildingHeight,
  buildingClassification,
  layers,
  styleLoadedCount,
}: UseBuildingLayersProps) => {
  // Handle building footprints rendering from buildingGeoJson
  useEffect(() => {
    if (!map.current || !mapLoaded || !buildingGeoJson) return

    const buildingLayer = layers.find(
      (layer) => layer.id === 'building-footprints'
    )
    const isVisible = buildingLayer?.visible ?? buildingLayer?.enabled ?? false

    // Get color based on building classification
    const buildingColor = getBuildingClassificationColor(buildingClassification)

    try {
      // Add source if it doesn't exist
      if (!map.current.getSource('building-footprints-source')) {
        map.current.addSource('building-footprints-source', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: buildingGeoJson,
            properties: {},
          },
        })
      }

      // Add layer if it doesn't exist
      if (!map.current.getLayer('building-footprints-layer')) {
        map.current.addLayer({
          id: 'building-footprints-layer',
          type: 'line',
          source: 'building-footprints-source',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': buildingColor,
            'line-width': 2,
            'line-opacity': 0.9,
          },
        })

        // Add fill layer for the building
        map.current.addLayer({
          id: 'building-footprints-fill',
          type: 'fill',
          source: 'building-footprints-source',
          paint: {
            'fill-color': buildingColor,
            'fill-opacity': 0.2,
          },
        })
      } else {
        // Update colors if classification changed
        map.current.setPaintProperty(
          'building-footprints-layer',
          'line-color',
          buildingColor
        )
        map.current.setPaintProperty(
          'building-footprints-fill',
          'fill-color',
          buildingColor
        )
      }

      // Update visibility based on layer state
      if (map.current.getLayer('building-footprints-layer')) {
        map.current.setLayoutProperty(
          'building-footprints-layer',
          'visibility',
          isVisible ? 'visible' : 'none'
        )
      }
      if (map.current.getLayer('building-footprints-fill')) {
        map.current.setLayoutProperty(
          'building-footprints-fill',
          'visibility',
          isVisible ? 'visible' : 'none'
        )
      }
    } catch (error) {
      console.warn('Failed to add building footprints:', error)
    }
  }, [
    buildingGeoJson,
    buildingClassification,
    layers,
    mapLoaded,
    styleLoadedCount,
  ])

  // Handle building 3D extrusion
  useEffect(() => {
    if (!map.current || !mapLoaded || !buildingGeoJson || !buildingHeight)
      return

    const building3DLayer = layers.find((layer) => layer.id === 'building-3d')
    const isVisible =
      building3DLayer?.visible ?? building3DLayer?.enabled ?? false

    // Get color based on building classification
    const buildingColor = getBuildingClassificationColor(buildingClassification)

    try {
      // Add layer if it doesn't exist
      if (!map.current.getLayer('building-3d-layer')) {
        map.current.addLayer({
          id: 'building-3d-layer',
          type: 'fill-extrusion',
          source: 'building-footprints-source',
          paint: {
            'fill-extrusion-color': buildingColor,
            'fill-extrusion-height': buildingHeight,
            'fill-extrusion-base': 0,
            'fill-extrusion-opacity': 0.7,
          },
        })
      } else {
        // Update color if classification changed
        map.current.setPaintProperty(
          'building-3d-layer',
          'fill-extrusion-color',
          buildingColor
        )
      }

      // Update visibility based on layer state
      if (map.current.getLayer('building-3d-layer')) {
        map.current.setLayoutProperty(
          'building-3d-layer',
          'visibility',
          isVisible ? 'visible' : 'none'
        )
      }
    } catch (error) {
      console.warn('Failed to add building 3D extrusion:', error)
    }
  }, [
    buildingGeoJson,
    buildingHeight,
    buildingClassification,
    layers,
    mapLoaded,
    styleLoadedCount,
  ])

  // Handle dimension metrics for building
  useEffect(() => {
    if (!map.current || !mapLoaded || !buildingGeoJson) return

    const dimensionLayer = layers.find(
      (layer) => layer.id === 'building-dimension-metrics'
    )
    const isVisible =
      dimensionLayer?.visible ?? dimensionLayer?.enabled ?? false

    // Get color based on building classification
    const buildingColor = getBuildingClassificationColor(buildingClassification)

    try {
      // Extract coordinates from buildingGeoJson
      const coordinates = extractCoordinates(buildingGeoJson)

      if (coordinates.length === 0) return

      // Get the outer ring
      const outerRing = coordinates[0]

      // Create dimension labels for each segment
      const dimensionLabels: any[] = []
      for (let i = 0; i < outerRing.length - 1; i++) {
        const start = outerRing[i]
        const end = outerRing[i + 1]
        const distance = calculateDistance(start, end)
        const midpoint = getMidpoint(start, end)

        dimensionLabels.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: midpoint,
          },
          properties: {
            distance: distance.toFixed(2),
            distanceLabel: `${distance.toFixed(1)}m`,
          },
        })
      }

      // Add building dimension metrics source
      if (!map.current.getSource('building-dimension-metrics-source')) {
        map.current.addSource('building-dimension-metrics-source', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: dimensionLabels,
          },
        })
      } else {
        // Update existing source
        const source = map.current.getSource(
          'building-dimension-metrics-source'
        ) as mapboxgl.GeoJSONSource
        source.setData({
          type: 'FeatureCollection',
          features: dimensionLabels,
        })
      }

      // Add or remove building dimension metrics layer based on visibility
      const layerExists = map.current.getLayer(
        'building-dimension-metrics-layer'
      )

      if (isVisible && !layerExists) {
        // Add layer when it should be visible and doesn't exist
        map.current.addLayer({
          id: 'building-dimension-metrics-layer',
          type: 'symbol',
          source: 'building-dimension-metrics-source',
          layout: {
            'text-field': ['get', 'distanceLabel'],
            'text-size': 12,
            'text-offset': [0, 0],
            'text-anchor': 'center',
            'text-allow-overlap': true,
          },
          paint: {
            'text-color': buildingColor,
            'text-halo-color': '#FFFFFF',
            'text-halo-width': 2,
          },
        })
      } else if (isVisible && layerExists) {
        // Update color if layer already exists and should be visible
        map.current.setPaintProperty(
          'building-dimension-metrics-layer',
          'text-color',
          buildingColor
        )
      } else if (!isVisible && layerExists) {
        // Remove layer when it should be hidden and exists
        map.current.removeLayer('building-dimension-metrics-layer')
      }
    } catch (error) {
      console.warn('Failed to add building dimension metrics:', error)
    }
  }, [buildingGeoJson, layers, mapLoaded, styleLoadedCount])
}
